import { getTimeWithOffset } from '@dispatch/shared';
import {
  Actions,
  BlobAttribute,
  Company,
  MultiSelectOption,
  ReassignFormBody,
  ResourceMeta,
  ResultStatus,
  Subjects,
  TaskCancelRequest,
  TaskEvent,
  TaskForm,
  TaskPeril,
  TaskResponse,
} from '@dispatch/shared/types';
import { Ability } from '@casl/ability';
import {
  DrawerComponent,
  DrawerComponentMeta,
  DrawerMeta,
  Form,
  RenderFuncProps,
  TabContainer,
  TabPanel,
  SelectField,
  ConfirmModal,
} from '@dispatch/web/ui';
import { addMinutes, format, parseISO } from 'date-fns';
import React, { useMemo } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Button,
  Fab,
  Typography,
} from '@mui/material';
import { isNil } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';
import { TaskDetailsContext } from '@dispatch/web/hooks';
import { useCompany } from '@dispatch/shared/hooks';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import { grey, indigo } from '@mui/material/colors';
import {
  GeoStatusChip,
  MethodOfInspectionChip,
  TaskTimelineTab,
  StyledFieldSet,
  GeneralInformation,
  formDefaults,
  formCancelDefaults,
  TaskAssignedChip,
  TaskDetailsProps,
} from '@dispatch/web/features/tasks';
import { useCurrentUserAsResourceId } from '@dispatch/shared/hooks';
import { AssignmentInformation } from '../components/AssignmentInformation';

export type ComplexLossDetails = {
  data?: TaskResponse;
  status: ResultStatus;
  onSave: (value: any) => void;
  onCancelTask: (value: string) => void;
  onLockTask: () => void;
  onStartTask: () => void;
  onCompleteTask: () => void;
  isSaving?: boolean;
  resources: any;
  permissions: Ability<[Actions, Subjects]> | undefined;
  currentTab: number;
  setTab: (newTab: number) => void;
  perils: any;
  showOnlyQualified: {
    value: boolean;
    setShowOnlyQualified: React.Dispatch<boolean>;
  };
  taskEvents?: TaskEvent[];
  getTaskEventsStatus: ResultStatus;
  rescheduleReasons?: MultiSelectOption[];
  notifyError: (
    title: string,
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  resourceMeta?: ResourceMeta;
  disableButtons?: boolean;
  isAdjuster?: boolean;
  geoStatus?: string;
  cancelStatus?: ResultStatus;
  company?: Company;
};

const TaskValidationSchema = yup.object().shape({
  resourceId: yup.string().nullable(),
  needsReason: yup.boolean().nullable(),
  reasonCode: yup.string().when('needsReason', {
    is: true,
    then: yup.string().required('Must specify a reschedule reason'),
  }),
  assignmentHandling: yup.object().shape({
    appointmentStart: yup
      .date()
      .test(
        'appointment_start_window',
        'Cannot move task start time from the initially scheduled window.',
        (value, context) => {
          const assignmentHandling = context.parent;
          if (!value) return true;
          return (
            value >= assignmentHandling.mustStartAfterTime &&
            value < assignmentHandling.mustEndBeforeTime
          );
        }
      ),
  }),
});

const taskCancelValidationSchema = yup.object().shape({
  cancelReason: yup.string().required('Must specify a cancel reason'),
});

const STARTED_QUEUE = 'wip_q';
const COMPLETED_QUEUE = 'egress';
const CANCELLED_QUEUE = 'cancelled_q';

export function ComplexLossDetails({
  data,
  onSave,
  onForceReassign,
  onCancelTask,
  onLockTask,
  onStartTask,
  onCompleteTask,
  status,
  isSaving = false,
  resources,
  permissions,
  currentTab,
  setTab,
  perils,
  showOnlyQualified,
  taskEvents,
  getTaskEventsStatus,
  notifyError,
  resourceMeta,
  rescheduleReasons,
  disableButtons,
  isAdjuster,
  geoStatus,
  cancelStatus,
  company,
}: TaskDetailsProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const params = useParams();
  const taskId = params['taskId'];
  const navigate = useNavigate();
  const { value: currentUserResourceId } = useCurrentUserAsResourceId();

  // complex-loss adjuster can only edit if they are assigned to the task
  // if not an adjuster then they should be able to edit if they have upsert permissions
  const canEdit = useMemo(() => {
    if (isAdjuster) {
      return (
        permissions?.can('upsert', 'complex-loss') &&
        data?.$msg?.assignedResource?.id === currentUserResourceId
      );
    } else return permissions?.can('upsert', 'complex-loss');
  }, [
    currentUserResourceId,
    data?.$msg?.assignedResource?.id,
    isAdjuster,
    permissions,
  ]);

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: { ...formDefaults },
    resolver: yupResolver(TaskValidationSchema),
  });

  const notifyValidationErrors = () => {
    if (!isNil(formErrors)) {
      const errors = Object.entries(formErrors);
      if (errors.length > 0) {
        notifyError(
          'Error',
          (errors[0] as any)?.[1]?.message ??
            'Please make sure all fields are valid'
        );
      }
    }
  };

  React.useEffect(() => {
    notifyValidationErrors();
  }, [formErrors]);

  const {
    control: formCancelControl,
    formState: formCancelState,
    handleSubmit: handleCancelSubmit,
    reset: resetCancel,
    watch,
  } = useForm({
    defaultValues: { ...formCancelDefaults },
    resolver: yupResolver(taskCancelValidationSchema),
  });

  const { cancelReasons, cancelReasonsStatus } = useCompany();

  const parseTaskDataForForm: any = React.useMemo(() => {
    if (!data) return;

    const startDateTime = new Date(
      data?.$msg?.assignmentHandling?.appointment || ''
    ).toJSON();
    const timeZoneId = data?.$msg?.claim?.lossLocation?.timeZoneId || 'unknown';
    const durationInMinutes = Math.trunc(
      (data?.$msg?.assignmentHandling?.durationInMinutes || 0) *
        (1 / (data?.$metadata?.appliedEfficiencyCoefficient || 1))
    );

    const startTime = !isNil(startDateTime)
      ? getTimeWithOffset(timeZoneId, startDateTime)
      : null;
    const endtime =
      startTime && !isNil(startTime)
        ? addMinutes(new Date(startTime), durationInMinutes)
        : null;

    const mustStartAfterTime = parseISO(
      data?.$msg?.assignmentHandling?.mustStartAfter
    );
    const mustEndBeforeTime = parseISO(
      data?.$msg?.assignmentHandling?.mustEndBefore
    );
    const mustStartAfter = !isNil(mustStartAfterTime)
      ? getTimeWithOffset(timeZoneId, mustStartAfterTime)
      : null;
    const mustEndBefore = !isNil(mustEndBeforeTime)
      ? getTimeWithOffset(timeZoneId, mustEndBeforeTime)
      : null;

    return {
      ...data,
      assignmentHandling: {
        ...data?.$msg?.assignmentHandling,
        SLO: 0,
        isStateLicenseRequired: true,
        lossTimeZone: timeZoneId,
        appointmentStart: startTime || '',
        appointmentEnd: endtime || '',
        mustStartAfterTime: mustStartAfter,
        mustEndBeforeTime: mustEndBefore,
        durationInMinutes: durationInMinutes,
      },
      attributes: [
        ...(data?.$msg?.attributes?.flatMap((attribute: BlobAttribute) => {
          if (attribute?.name) {
            return attribute.id;
          }
          return [];
        }) || []),
      ],
      claim: {
        ...data?.$msg?.claim,
        timeZone: data?.$msg?.claim?.lossLocation?.timeZoneId || 'unknown',
        locations: [
          {
            locationType: 'lossLocation',
            location: `${data?.$msg?.claim?.lossLocation?.streetLine1}${
              data?.$msg?.claim?.lossLocation?.streetLine2
                ? data?.$msg?.claim?.lossLocation?.streetLine2
                : ''
            }, ${data?.$msg?.claim?.lossLocation?.city} ${
              data?.$msg?.claim?.lossLocation?.state
            } ${data?.$msg?.claim?.lossLocation?.zip}`,
          },
        ],
      },
      contact: {
        ...data?.$msg?.contact,
        primary: {
          ...data?.$msg?.contact?.primary,
          insuredName: data?.$msg?.contact?.primary?.name,
          notes: '',
        },
        secondary: {
          ...data?.$msg?.contact?.secondary,
          insuredName: data?.$msg?.contact?.secondary?.name,
          notes: '',
        },
      },
    };
  }, [data]);

  React.useEffect(() => {
    reset(parseTaskDataForForm);
    if (status === 'success') {
      setIsLoading(false);
    }
  }, [data, reset, parseTaskDataForForm, status]);

  const handleReassign = ({
    resourceId,
    reasonCode,
    forceReassign,
  }: {
    resourceId: string;
    reasonCode: string;
    forceReassign: boolean;
  }) => {
    if (forceReassign && onForceReassign) {
      onForceReassign(resourceId, reasonCode);
    } else {
      const reassignFormBody: ReassignFormBody = {
        resourceId: resourceId,
        reasonCode: reasonCode,
        assignmentHandling: {
          appointmentStart:
            parseTaskDataForForm.assignmentHandling?.appointmentStart,
          appointmentEnd:
            parseTaskDataForForm.assignmentHandling?.appointmentEnd,
        },
        claim: {
          timeZone: parseTaskDataForForm.claim?.timeZone,
        },
      };

      onSave(reassignFormBody);
    }
  };

  const handleSave = () => {
    handleSubmit((values: any) => {
      reset({ ...values });
      return onSave(values);
    })();
  };

  const renderTitle = () => {
    if (taskId) {
      return status === 'loading' ? '' : data?.$msg?.claim?.insuredName || '--';
    }

    return 'Add a New Task';
  };

  const renderGeneralInformation = (props: {
    currentTab: number;
    control: Control<FieldValues>;
    errors: FieldErrors;
    permissions: Ability<[Actions, Subjects]> | undefined;
  }) => {
    return <GeneralInformation {...props} />;
  };

  const renderTimeline = ({ currentTab }: { currentTab: number }) => {
    return (
      <TabPanel value={currentTab} index={2} data-testid="general-info-tab">
        <Box sx={{ padding: '24px !important' }}>
          <TaskTimelineTab data={taskEvents} status={getTaskEventsStatus} />
        </Box>
      </TabPanel>
    );
  };

  const attributeOptions = () => {
    return data?.$msg?.attributes?.flatMap?.(
      (item: any): { title: string; value: string } | [] => {
        if (item.name) {
          return {
            title: `${item.name} - ${item.value}` || '',
            value: item.id,
          };
        }
        return [];
      }
    );
  };

  const renderTaskLock = () => (
    <FormControlLabel
      disabled={
        !canEdit ||
        data?.$metadata?.targetQueue === 'egress' ||
        data?.$metadata?.targetQueue === 'wip_q'
      }
      control={
        <Switch
          checked={
            data?.$msg?.lockedAt !== null && data?.$msg?.lockedAt !== undefined
          }
          onClick={onLockTask}
          size={'small'}
        />
      }
      label={'Locked'}
      labelPlacement={'start'}
    />
  );

  const renderAssignmentInformation = (props: {
    currentTab: number;
    control: Control<FieldValues>;
    errors: FieldErrors;
  }) => {
    return (
      <AssignmentInformation
        {...props}
        company={company}
        handleReassign={handleReassign}
        rescheduleReasons={rescheduleReasons}
        taskData={data}
        setValue={setValue}
        getValues={getValues}
        resourceData={data?.resource}
        resources={resources}
        permissions={permissions}
        resourceMeta={resourceMeta}
        attributeOptions={
          data && data.$msg?.attributes?.length > 0 ? attributeOptions() : []
        }
        inspectionOptions={[
          {
            title: data?.$msg?.assignmentHandling?.methodOfInspection || '',
            value: data?.$msg?.assignmentHandling?.methodOfInspection || '',
          },
        ]}
        perils={perils?.map((x: TaskPeril) => {
          return { title: x.description, value: x.code };
        })}
        renderTaskLock={renderTaskLock}
      />
    );
  };

  const showCancel = React.useMemo(() => {
    const canCancelTask = (targetQueue: string | undefined) => {
      const cancelTaskExclusionQueues: string[] = [
        'cancelled_q',
        'input_q',
        'egress',
      ];
      return (
        cancelTaskExclusionQueues.find((queue) => queue === targetQueue) ===
        undefined
      );
    };

    return canEdit && canCancelTask(data?.$msg?.assignmentHandling.targetQueue);
  }, [permissions, data?.$msg?.assignmentHandling.targetQueue]);

  const [showCancelReasonModal, setShowCancelReasonModal] =
    React.useState(false);
  const renderCancelContent: React.ReactElement = (
    <Form<TaskCancelRequest>
      errors={formCancelState.errors}
      control={formCancelControl}
      handleSubmit={handleCancelSubmit}
      render={({ control, errors }: RenderFuncProps) => {
        return (
          <>
            <p>
              Do you really want to <b>Cancel?</b> You must select a reason
            </p>
            <Grid container spacing={1} alignItems="baseline">
              <Grid item xs={8}>
                {cancelReasonsStatus === 'error' && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Failed to load cancel reasons
                  </Typography>
                )}
                <SelectField
                  id="cancelReason"
                  name="cancelReason"
                  label="Reason"
                  control={control}
                  errors={errors}
                  options={cancelReasons || []}
                />
              </Grid>
            </Grid>
          </>
        );
      }}
    />
  );

  const renderDeactivateModal = () => {
    return (
      <ConfirmModal
        actionStatus={cancelStatus}
        onConfirm={() => {
          const reason = watch('cancelReason') ?? '';
          onCancelTask(reason);
        }}
        onDeny={() => {
          resetCancel();
          setShowCancelReasonModal(false);
        }}
        confirmContent={renderCancelContent}
        confirmTitle={'Cancel Task'}
        modalType={'cancelTask'}
      />
    );
  };

  const currentQueue = React.useMemo(() => {
    return data?.$metadata?.targetQueue;
  }, [data?.$metadata?.targetQueue]);

  const isStarted = currentQueue === STARTED_QUEUE;
  const isComplete = currentQueue === COMPLETED_QUEUE;
  const isCancelled = currentQueue === CANCELLED_QUEUE;

  React.useEffect(() => {
    setShowCancelReasonModal(false);
  }, [currentQueue]);

  const renderCancelButton = () => {
    return (
      <Stack px={1}>
        <Button
          data-testid="cancel-task-button"
          type="button"
          variant="outlined"
          color="error"
          onClick={handleCancelTask}
          disabled={!showCancel || isCancelled}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Cancel Task
        </Button>
      </Stack>
    );
  };

  const handleCancelTask = () => {
    setShowCancelReasonModal(true);
  };

  const taskStateFabButton = (
    title: string,
    icon: React.ReactElement,
    onClick: () => void,
    disabled: boolean
  ) => {
    return (
      <Stack
        data-testid="action-fab-button-container"
        direction={'row'}
        gap={1}
        alignItems="center"
        marginRight="4px"
      >
        <Typography
          variant="caption"
          fontSize="14px"
          boxShadow={
            !disabled
              ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
              : undefined
          }
          borderRadius="4px"
          sx={{
            backgroundColor: !disabled ? '#fff' : 'rgb(235, 235, 228)',
            padding: '2px 6px',
          }}
        >
          {title}
        </Typography>
        <Fab
          data-testid="action-fab-button"
          disabled={disabled}
          onClick={!disabled ? onClick : undefined}
          size="medium"
          sx={{
            '&.MuiFab-root': {
              backgroundColor: disabled ? 'rgb(235, 235, 228)' : undefined,
            },
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            color: 'black',
            background: !disabled ? grey[50] : 'rgb(235, 235, 228)',
            '&:hover': {
              //you want this to be the same as the backgroundColor above
              backgroundColor: 'rgb(235, 235, 228)',
            },
          }}
          aria-label="add"
        >
          {icon}
        </Fab>
      </Stack>
    );
  };
  const [showFabButtons, setShowFabButtons] = React.useState(false);
  const renderFabButton = React.useMemo(() => {
    return (
      <Stack
        data-testid="task-fab-button-container"
        onMouseLeave={() => setShowFabButtons(false)}
        alignItems="flex-end"
        sx={{
          position: 'absolute',
          bottom: 60,
          right: 20,
          gap: 2,
        }}
      >
        {showFabButtons && (
          <>
            {taskStateFabButton(
              'Start',
              <ContentPasteGoOutlinedIcon />,
              onStartTask,
              status === 'loading' || disableButtons || isStarted || isComplete
            )}
            {taskStateFabButton(
              'Complete',
              <AssignmentTurnedInOutlinedIcon />,
              onCompleteTask,
              status === 'loading' || disableButtons || isComplete || !isStarted
            )}
            {taskStateFabButton(
              'Cancel',
              <AssignmentLateOutlinedIcon />,
              handleCancelTask,
              !showCancel || isCancelled || isComplete || !isAdjuster
            )}
          </>
        )}

        <Fab
          data-testid="task-fab-button"
          disabled={isComplete || isCancelled}
          onClick={() => setShowFabButtons(!showFabButtons)}
          sx={{
            color: 'white',
            '&.MuiFab-root': {
              backgroundColor:
                isComplete || isCancelled ? 'rgb(235, 235, 228)' : indigo[700],
            },
          }}
          aria-label="add"
        >
          <ContentPasteIcon />
        </Fab>
      </Stack>
    );
  }, [showFabButtons, isComplete, isStarted, isCancelled, showCancel]);
  const cancelTaskButton = React.useMemo(() => {
    return !isAdjuster ? renderCancelButton() : [];
  }, [isAdjuster]);

  return (
    <TaskDetailsContext.Provider
      value={{
        value: showOnlyQualified?.value,
        setShowOnlyQualified: showOnlyQualified?.setShowOnlyQualified,
      }}
    >
      <DrawerComponent
        // the loading state from react query is ending before the fields are able to resolve
        // isLoading is used to control when the loading actually should finish.
        status={isLoading ? 'loading' : 'success'}
        onSave={handleSave}
        saveDisabled={isSaving ? true : !isDirty}
        // complex-loss adjuster can only edit if they are assigned to the task
        // other roles that have upsert permissions that are not adjuster should still be able to edit
        showButtons={canEdit}
        onClose={() => navigate('/complex-loss')}
        title={renderTitle()}
        showSaveButton={!!permissions?.can?.('upsert', 'complex-loss')}
        renderLeftSideButtons={[cancelTaskButton]}
        sx={{
          width: '65vw',
          '& .MuiBox-root': {
            padding: 0,
          },
        }}
        metas={[
          <DrawerMeta
            title="DATE OF LOSS"
            subtitle={
              data?.$msg?.claim.dateOfLoss
                ? format(new Date(data?.$msg?.claim.dateOfLoss), 'MM/dd/yyyy')
                : format(new Date(), 'MM/dd/yyyy')
            }
          />,
          <DrawerMeta
            title="CLAIM #"
            subtitle={data?.$msg?.claim?.claimNumber || '--'}
          />,
          <DrawerComponentMeta
            component={
              <Stack direction="row" gap={1}>
                <GeoStatusChip geoStatus={geoStatus || ''} />
                <TaskStatusChip
                  targetQueue={data?.$metadata?.targetQueue}
                  cancelledReason={data?.$msg?.status?.cancelled?.reason}
                  resourceId={data?.$msg?.assignedResource?.id}
                />
                <MethodOfInspectionChip
                  methodOfInspection={
                    data?.$msg?.assignmentHandling?.methodOfInspection || ''
                  }
                  variant="filled"
                  size="medium"
                />
              </Stack>
            }
          />,
        ]}
        renderTabs={
          <TabContainer
            tabLabels={['ASSIGNMENT', 'GENERAL', 'TIMELINE']}
            setTab={setTab}
            currentTab={currentTab}
          />
        }
        isActive={data?.$metadata?.targetQueue !== 'cancelled_q'}
      >
        <Form<Partial<TaskForm>>
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <StyledFieldSet
                disabled={
                  data?.$metadata?.targetQueue === 'cancelled_q'
                    ? true
                    : !canEdit
                }
              >
                {renderAssignmentInformation({
                  currentTab: currentTab,
                  control,
                  errors,
                })}
                {renderGeneralInformation({
                  currentTab: currentTab,
                  control,
                  errors,
                  permissions,
                })}
                {renderTimeline({ currentTab: currentTab })}
              </StyledFieldSet>
            );
          }}
        />
        {canEdit && isAdjuster && renderFabButton}
      </DrawerComponent>
      {showCancelReasonModal && renderDeactivateModal()}
    </TaskDetailsContext.Provider>
  );
}

export const TaskStatusChip = (props: {
  targetQueue?: string;
  cancelledReason?: string;
  resourceId?: string;
}) => {
  const { cancelReasons } = useCompany();

  if (props.targetQueue === 'cancelled_q') {
    let cancelReasonTitle: string | undefined;

    if (props.cancelledReason) {
      const found = cancelReasons?.find(
        (r) =>
          r.value === props.cancelledReason || r.title === props.cancelledReason
      );
      if (found) cancelReasonTitle = found.title;
    }

    if (!cancelReasonTitle) {
      switch (props.cancelledReason) {
        case 'coverageDenied':
          cancelReasonTitle = 'Coverage Denied';
          break;
        case 'alternateMoiUsed':
          cancelReasonTitle = 'Alternate MOI used';
          break;
        case 'claimWithdrawal':
          cancelReasonTitle = 'Claim Withdrawal/Under Deductible';
          break;
        case 'customerNotAvailable':
          cancelReasonTitle = 'Customer/Vendor not available';
          break;
        case 'weather':
          cancelReasonTitle = 'Weather';
          break;
        default:
          cancelReasonTitle = props.cancelledReason || 'Other';
          break;
      }
    }

    return (
      <Chip
        label={`Cancelled - ${cancelReasonTitle}`}
        color="error"
        size="medium"
        data-testid="task-status"
      />
    );
  }

  return (
    <TaskAssignedChip
      resourceId={props.resourceId}
      targetQueue={props.targetQueue || ''}
    />
  );
};
